import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useThemeStore } from "./store/themeStore";
import { registerServiceWorker } from "./lib/pushNotifications";

// Layout
import { DesktopShell } from "./components/layout/DesktopShell";

// Auth
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { LoginPage } from "./components/pages/LoginPage";
import { AuthCallback } from "./components/pages/AuthCallback";

// Pages
import { DashboardPage } from "./components/pages/DashboardPage";
import { MasterPage } from "./components/pages/MasterPage";
import { TransactionsPage } from "./components/pages/TransactionsPage";
import { CompactPage } from "./components/pages/CompactPage";
import { TrialBalancePage } from "./components/pages/TrialBalancePage";
import { SettlementPage } from "./components/pages/SettlementPage";
import { ModernSettlementPage } from "./components/pages/ModernSettlementPage";
import { MetaPage } from "./components/pages/MetaPage";
import { MCXPage } from "./components/pages/MCXPage";
import { UtilitiesPage } from "./components/pages/UtilitiesPage";
import { SuperAdminPage } from "./components/pages/SuperAdminPageNew";
import { QuickEntryPage } from "./components/pages/QuickEntryPage";

// New P1/P2/P3 Pages
import { AuditLogPage } from "./components/pages/AuditLogPage";
import { MarketingPage } from "./components/pages/MarketingPage";
import { GrowthPage } from "./components/pages/GrowthPage";
import { ProfilePage } from "./components/pages/ProfilePage";
import { PaymentsPage } from "./components/pages/PaymentsPage";
import { ExportPage } from "./components/pages/ExportPage";
import { MondaySettlingPage } from "./components/pages/MondaySettlingPage";
import { ReconciliationPage } from "./components/pages/ReconciliationPage";
import { NetResultPage } from "./components/pages/NetResultPage";
import { PlanSelectionPage } from "./components/pages/PlanSelectionPage";
import { CheckoutPage } from "./components/pages/CheckoutPage";

// Handle session_id in URL hash before route detection
function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in hash - process before ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LoginPage />} />
      
      {/* Protected Routes with Desktop Shell */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
      </Route>
      
      <Route path="/master" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<MasterPage />} />
      </Route>
      
      <Route path="/transactions" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<TransactionsPage />} />
      </Route>
      
      <Route path="/compact" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<CompactPage />} />
      </Route>
      
      <Route path="/trial-balance" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<TrialBalancePage />} />
      </Route>
      
      <Route path="/settlement" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<SettlementPage />} />
      </Route>
      
      <Route path="/modern-settlement" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<ModernSettlementPage />} />
      </Route>
      
      <Route path="/monday-settling" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<MondaySettlingPage />} />
      </Route>
      
      <Route path="/meta" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<MCXPage />} />
      </Route>
      
      <Route path="/utilities" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<UtilitiesPage />} />
      </Route>

      <Route path="/settings" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<UtilitiesPage />} />
      </Route>

      <Route path="/admin" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<SuperAdminPage />} />
      </Route>

      <Route path="/quick-entry" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<QuickEntryPage />} />
      </Route>

      {/* New P1/P2/P3 Routes */}
      <Route path="/audit-logs" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<AuditLogPage />} />
      </Route>

      <Route path="/marketing" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<MarketingPage />} />
      </Route>

      <Route path="/growth" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<GrowthPage />} />
      </Route>

      <Route path="/profile" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<ProfilePage />} />
      </Route>

      <Route path="/payments" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<PaymentsPage />} />
      </Route>

      <Route path="/export" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<ExportPage />} />
      </Route>

      <Route path="/reconciliation" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<ReconciliationPage />} />
      </Route>

      <Route path="/net-result" element={
        <ProtectedRoute>
          <DesktopShell />
        </ProtectedRoute>
      }>
        <Route index element={<NetResultPage />} />
      </Route>

      {/* Subscription Routes - No DesktopShell wrapper */}
      <Route path="/select-plan" element={
        <ProtectedRoute>
          <PlanSelectionPage />
        </ProtectedRoute>
      } />
      
      <Route path="/checkout/:planId" element={
        <ProtectedRoute>
          <CheckoutPage />
        </ProtectedRoute>
      } />

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const { initTheme } = useThemeStore();

  useEffect(() => {
    initTheme();
    // Register service worker for push notifications
    registerServiceWorker();
  }, [initTheme]);

  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
